"use client";

import { Popover } from "antd";
import { useContext, useState } from "react";
import { CMSModal } from "@/context";
import { ChevronDown, ChevronUp } from "lucide-react";
import { twMerge } from "tailwind-merge";

interface FieldOption {
  id: string;
  label: string;
  icon?: React.ReactNode;
  disabled?: boolean;
}

interface AddFieldDropdownProps {
  options: FieldOption[];
  onSelect?: (field: FieldOption) => void;
  buttonLabel?: string;
}

export default function DropDown({
  options,
  onSelect,
  buttonLabel = "Add Field",
}: AddFieldDropdownProps) {
  const { theme } = useContext(CMSModal);
  const [open, setOpen] = useState(false);

  const handleSelect = (opt: FieldOption) => {
    if (opt.disabled) return;
    onSelect?.(opt);
    setOpen(false);
  };

  const content = (
    <div
      className={twMerge(
        "rounded-md w-36",
        theme === "dark" ? "bg-[#262727]" : "bg-white"
      )}
    >
      {options.map((opt) => (
        <button
          key={opt.id}
          disabled={opt.disabled}
          onClick={() => handleSelect(opt)}
          className={twMerge(
            "flex w-full justify-start items-center gap-3 w-36 p-2 text-sm transition-colors",
            opt.disabled
              ? "opacity-50 cursor-not-allowed text-gray-400"
              : theme === "dark"
                ? "text-gray-300 hover:bg-gray-800"
                : "hover:bg-gray-50 "
          )}
        >
          {opt.icon}
          {opt.label}
        </button>
      ))}
    </div>
  );

  return (
    <Popover
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      content={content}
      placement="bottom"
    >
      <button
        className={twMerge(
          "inline-flex items-center rounded-md border border-[#099ed1]  px-4 py-2 text-sm font-medium text-[#099ed1] transition-colors",
          theme === "dark"
            ? "bg-transparent hover:bg-[#1a3a4a]"
            : "bg-white hover:bg-blue-50"
        )}
      >
        {buttonLabel}
        {open ? <ChevronUp className="ml-6 h-4 w-4" /> : <ChevronDown className="ml-6 h-4 w-4" />}
      </button>
    </Popover>
  );
}
